import { api, skipAuthRefreshConfig } from '@/lib/api/axios';
import { extractAuthorizationSummary, extractPermissionCodes, normalizePermissionCodes } from '@/lib/permissions';
import { CompanyService, resolveActiveCompanyPayload } from '@/services/company.service';
import type { MyCompanyResponse, User, UserAuthorizationSummary } from '@/types';
import type { ChangePasswordDto, RegisterUserDto, SignInUserDto } from '@/types/dto/auth.dto';

async function loadMyPermissionsSummary(): Promise<UserAuthorizationSummary | null> {
  const response = await api.get<unknown>('/auth/my-permissions', {
    headers: {
      'Cache-Control': 'no-store',
      Expires: '0',
      'If-None-Match': '',
      'If-Modified-Since': '0',
      Pragma: 'no-cache',
    },
    params: { _t: Date.now() },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
  });

  if (response.status === 404) {
    return null;
  }

  const { data } = response;
  if (Array.isArray(data)) {
    return {
      permissionCodes: normalizePermissionCodes(data),
      permissionCatalog: [],
    };
  }

  return extractAuthorizationSummary(data);
}

export const AuthService = {
  signIn: async (data: SignInUserDto) => {
    await api.post('/auth/signin', data, skipAuthRefreshConfig);
  },

  register: async (data: RegisterUserDto) => {
    await api.post('/auth/signup', data, skipAuthRefreshConfig);
  },

  logout: async () => {
    await api.post('/auth/logout', {}, skipAuthRefreshConfig);
  },

  setActiveCompany: async (companyId: string) => {
    await api.post('/auth/select-company', { companyId });
  },

  changePassword: async (data: ChangePasswordDto) => {
    await api.post('/auth/change-password', data);
  },

  requestPasswordResetCode: async (data: { socialNumber: string }) => {
    await api.post('/auth/password-reset/request-code', data, skipAuthRefreshConfig);
  },

  confirmPasswordReset: async (data: { socialNumber: string; code: string; newPassword: string }) => {
    await api.post('/auth/password-reset/confirm', data, skipAuthRefreshConfig);
  },

  myPermissions: async (): Promise<string[]> => (await loadMyPermissionsSummary())?.permissionCodes ?? [],

  myPermissionSummary: async (): Promise<UserAuthorizationSummary | null> => loadMyPermissionsSummary(),

  profile: async (): Promise<User> => {
    const [profileResponse, activeCompanyResponse] = await Promise.all([
      api.get<User | { user: User }>('/auth/profile', {
        headers: {
          'Cache-Control': 'no-store',
          Expires: '0',
          'If-None-Match': '',
          'If-Modified-Since': '0',
          Pragma: 'no-cache',
        },
        params: { _t: Date.now() },
      }),
      CompanyService.getMyCompany(),
    ]);

    const { data } = profileResponse;
    const activeCompany =
      activeCompanyResponse.status === 401 || activeCompanyResponse.status === 404
        ? null
        : resolveActiveCompanyPayload(activeCompanyResponse.data);
    const authorizationFromEndpoint = activeCompany ? await loadMyPermissionsSummary() : null;
    const permissionCodesFromEndpoint = authorizationFromEndpoint?.permissionCodes ?? [];

    const rootPayload = (data && typeof data === 'object' ? (data as Record<string, unknown>) : {}) as Record<
      string,
      unknown
    >;
    const userPayload = (() => {
      const nested = rootPayload.user;
      if (nested && typeof nested === 'object') {
        return nested as Record<string, unknown>;
      }
      return rootPayload;
    })();

    const authorizationFromProfile = extractAuthorizationSummary({
      ...rootPayload,
      ...userPayload,
    });
    const permissionCodesFromProfile =
      authorizationFromProfile?.permissionCodes ??
      extractPermissionCodes({
        ...rootPayload,
        ...userPayload,
      });
    const permissionCodes =
      permissionCodesFromEndpoint.length > 0 ? permissionCodesFromEndpoint : permissionCodesFromProfile;
    const authorization = authorizationFromEndpoint ?? authorizationFromProfile;

    const user = userPayload as unknown as User & {
      cpf?: string;
      mustChangePassword?: boolean;
      shouldChangePassword?: boolean;
      isTemporaryPassword?: boolean;
      isSuperAdmin?: boolean;
      superAdmin?: boolean;
      isSuperuser?: boolean;
      isSuperUser?: boolean;
      systemRole?: string;
    };
    const isSuperAdmin = Boolean(
      user.isSuperAdmin || user.superAdmin || user.isSuperuser || user.isSuperUser || user.systemRole === 'SUPER_ADMIN',
    );

    return {
      ...user,
      socialNumber: user.socialNumber ?? user.cpf ?? '',
      mustChangePassword: Boolean(user.mustChangePassword ?? user.shouldChangePassword ?? user.isTemporaryPassword),
      permissionCodes,
      ...(authorization
        ? {
            authorization: {
              ...authorization,
              permissionCodes,
            },
          }
        : {}),
      isSuperAdmin,
    };
  },

  companies: async (): Promise<MyCompanyResponse[]> => {
    const response = await api.get<MyCompanyResponse[] | { companies: MyCompanyResponse[] }>('/auth/my-companies', {
      headers: {
        'Cache-Control': 'no-store',
        Expires: '0',
        'If-None-Match': '',
        'If-Modified-Since': '0',
        Pragma: 'no-cache',
      },
      params: { _t: Date.now() },
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });

    if (response.status === 404) {
      return [];
    }

    const { data } = response;

    if (data && typeof data === 'object' && 'companies' in data) {
      return data.companies;
    }
    return data as MyCompanyResponse[];
  },
};
